import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { fn } from "@ember/helper";
import { action } from "@ember/object";
import { service } from "@ember/service";
import EmojiPicker from "discourse/components/emoji-picker";
import Form from "discourse/components/form";
import { popupAjaxError } from "discourse/lib/ajax-error";
import { isTesting } from "discourse/lib/environment";
import { i18n } from "discourse-i18n";
import {
  CHAT_ATTRS,
  CHAT_QUICK_REACTION_TYPE_CUSTOM,
  CHAT_QUICK_REACTION_TYPE_FREQUENT,
  CHAT_QUICK_REACTIONS_CUSTOM_DEFAULT,
  CHAT_SEPARATE_SIDEBAR_MODE_ALWAYS,
  CHAT_SEPARATE_SIDEBAR_MODE_FULLSCREEN,
  CHAT_SEPARATE_SIDEBAR_MODE_NEVER,
} from "discourse/plugins/chat/discourse/lib/chat-constants";

export default class Chat extends Component {
  @service siteSettings;

  @tracked saved = false;

  get chatQuickReactionTypes() {
    return [
      {
        label: i18n("chat.quick_reaction_type.options.frequent"),
        value: CHAT_QUICK_REACTION_TYPE_FREQUENT,
      },
      {
        label: i18n("chat.quick_reaction_type.options.custom"),
        value: CHAT_QUICK_REACTION_TYPE_CUSTOM,
      },
    ];
  }

  get chatSeparateSidebarModeOptions() {
    return [
      {
        name: i18n("admin.site_settings.chat_separate_sidebar_mode.always"),
        value: CHAT_SEPARATE_SIDEBAR_MODE_ALWAYS,
      },
      {
        name: i18n("admin.site_settings.chat_separate_sidebar_mode.fullscreen"),
        value: CHAT_SEPARATE_SIDEBAR_MODE_FULLSCREEN,
      },
      {
        name: i18n("admin.site_settings.chat_separate_sidebar_mode.never"),
        value: CHAT_SEPARATE_SIDEBAR_MODE_NEVER,
      },
    ];
  }

  get formData() {
    const userOption = this.args.model.user_option;
    const emojis = (
      userOption.chat_quick_reactions_custom ||
      CHAT_QUICK_REACTIONS_CUSTOM_DEFAULT
    ).split("|");

    return {
      chat_enabled: userOption.chat_enabled,
      chat_quick_reaction_type: userOption.chat_quick_reaction_type,
      chat_quick_reactions_custom: emojis,
      chat_announce_new_messages: userOption.chat_announce_new_messages,
      chat_new_message_sound: userOption.chat_new_message_sound,
      chat_separate_sidebar_mode: userOption.chat_separate_sidebar_mode,
    };
  }

  @action
  handleEmojiSet(index, field, selectedEmoji) {
    let newValue = [...field.value];
    newValue[index] = selectedEmoji;
    field.set(newValue);
  }

  @action
  handleSubmit(data) {
    this.saved = false;

    const { chat_quick_reactions_custom, ...userOptions } = data;
    const shouldReload =
      userOptions.chat_enabled !== this.args.model.user_option.chat_enabled;

    this.args.model.set(
      "user_option.chat_quick_reactions_custom",
      chat_quick_reactions_custom.join("|")
    );

    for (const [key, value] of Object.entries(userOptions)) {
      this.args.model.set(`user_option.${key}`, value);
    }
    return this.args.model
      .save(CHAT_ATTRS)
      .then(() => {
        this.saved = true;
        if (shouldReload && !isTesting()) {
          location.reload();
        }
      })
      .catch(popupAjaxError);
  }

  <template>
    <Form
      @data={{this.formData}}
      @onSubmit={{this.handleSubmit}}
      as |form data|
    >
      <form.Field
        @format="large"
        @name="chat_enabled"
        @title={{i18n "chat.enable"}}
        @type="checkbox"
        as |field|
      >
        <field.Control @value={{field.value}} />
      </form.Field>

      <form.Section @title={{i18n "chat.personalization_title"}}>
        {{#if this.siteSettings.enable_emoji}}
          <form.Field
            @format="large"
            @name="chat_quick_reaction_type"
            @title={{i18n "chat.quick_reaction_type.title"}}
            @type="radio-group"
            as |field|
          >
            <field.Control as |radioGroup|>
              {{#each this.chatQuickReactionTypes as |option|}}
                <radioGroup.Radio @value={{option.value}}>
                  {{option.label}}
                </radioGroup.Radio>
              {{/each}}
            </field.Control>
          </form.Field>

          {{#if (eq data.chat_quick_reaction_type "custom")}}
            <form.Field
              @format="large"
              @name="chat_quick_reactions_custom"
              @title={{i18n "chat.quick_reaction_type.options.custom"}}
              @type="custom"
              as |field|
            >
              <field.Control>
                {{#each data.chat_quick_reactions_custom as |emoji index|}}
                  <EmojiPicker
                    @btnClass="btn-default"
                    @context="chat_preferences"
                    @didSelectEmoji={{fn this.handleEmojiSet index field}}
                    @emoji={{emoji}}
                  />
                {{/each}}
              </field.Control>
            </form.Field>
          {{/if}}
        {{/if}}
        <form.Field
          @format="large"
          @name="chat_separate_sidebar_mode"
          @title={{i18n "chat.separate_sidebar_mode.title"}}
          @type="select"
          as |field|
        >
          <field.Control @includeNone={{false}} as |select|>
            {{#each this.chatSeparateSidebarModeOptions as |option|}}
              <select.Option @value={{option.value}}>
                {{option.name}}
              </select.Option>
            {{/each}}
          </field.Control>
        </form.Field>
      </form.Section>
      <form.Section @title={{i18n "chat.accessibility_title"}}>
        <form.Field
          @format="large"
          @name="chat_announce_new_messages"
          @title={{i18n "chat.announce_new_messages.title"}}
          @type="checkbox"
          as |field|
        >
          <field.Control @value={{field.value}} />
        </form.Field>
        <form.Field
          @format="large"
          @name="chat_new_message_sound"
          @title={{i18n "chat.new_message_sound.title"}}
          @type="checkbox"
          as |field|
        >
          <field.Control @value={{field.value}} />
        </form.Field>
      </form.Section>
      <div class="save-controls">
        <form.Submit />
        {{#if this.saved}}
          <span class="saved">{{i18n "saved"}}</span>
        {{/if}}
      </div>
    </Form>
  </template>
}
